import { Text, TextInput, Stack, Loader, Badge, Box, Select, Group, UnstyledButton } from '@mantine/core';
import { Check, X, ArrowRight } from 'react-feather';
import type { CohortDetailsStepProps } from './types';
import { useDebouncedCallback } from '@mantine/hooks';
import { MEETING_PLATFORM_OPTIONS } from '../../../config/cohortConfig';
import type { MeetingPlatform } from '../../../types';
import classes from './CreateCohortPage.module.css';

export function CohortDetailsStep({
  form,
  checkingCode,
  codeAvailable,
  onCheckCode,
  checkingName,
  nameAvailable,
  onCheckName,
  checkingShortName,
  shortNameAvailable,
  onCheckShortName,
  internalUsers,
  jobTitleGroups,
}: CohortDetailsStepProps) {
  // Debounce code availability check
  const debouncedCheckCode = useDebouncedCallback((code: string) => {
    if (code.length >= 2) {
      onCheckCode(code);
    }
  }, 500);

  // Debounce name availability check
  const debouncedCheckName = useDebouncedCallback((name: string) => {
    if (name.trim().length >= 2) {
      onCheckName(name);
    }
  }, 500);

  // Debounce short name availability check
  const debouncedCheckShortName = useDebouncedCallback((shortName: string) => {
    if (shortName.trim().length >= 2) {
      onCheckShortName(shortName);
    }
  }, 500);

  const handleCodeChange = (value: string) => {
    // Auto-uppercase and remove invalid characters
    const cleanedCode = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    form.setFieldValue('code', cleanedCode);

    // Check availability if valid length
    if (cleanedCode.length >= 2) {
      debouncedCheckCode(cleanedCode);
    }
  };

  const handleNameChange = (value: string) => {
    form.setFieldValue('name', value);
    if (value.trim().length >= 2) {
      debouncedCheckName(value);
    }
  };

  const handleShortNameChange = (value: string) => {
    form.setFieldValue('shortName', value);
    if (value.trim().length >= 2) {
      debouncedCheckShortName(value);
    }
  };

  const renderAvailabilityStatus = (checking: boolean, available: boolean | null, value: string, minLength = 2) => {
    if (!value || value.trim().length < minLength) return null;

    if (checking) {
      return <Loader size="xs" />;
    }

    if (available === true) {
      return (
        <Badge color="green" size="sm" leftSection={<Check size={12} />}>
          Available
        </Badge>
      );
    }

    if (available === false) {
      return (
        <Badge color="red" size="sm" leftSection={<X size={12} />}>
          Taken
        </Badge>
      );
    }

    return null;
  };

  return (
    <Stack gap="lg">
      <TextInput
        label="Cohort Name"
        placeholder="e.g., Q1 Leadership"
        value={form.values.name}
        onChange={(e) => handleNameChange(e.currentTarget.value)}
        error={form.errors.name || (nameAvailable === false ? 'This cohort name is already in use' : undefined)}
        rightSection={renderAvailabilityStatus(checkingName, nameAvailable, form.values.name)}
        rightSectionWidth={100}
        required
      />

      <TextInput
        label="Cohort Short Name"
        placeholder="e.g., Lead01"
        value={form.values.shortName}
        onChange={(e) => handleShortNameChange(e.currentTarget.value)}
        error={form.errors.shortName || (shortNameAvailable === false ? 'This short name is already in use' : undefined)}
        rightSection={renderAvailabilityStatus(checkingShortName, shortNameAvailable, form.values.shortName)}
        rightSectionWidth={100}
        maxLength={50}
        required
      />

      <TextInput
        label="Cohort Code"
        placeholder="e.g., AC17"
        value={form.values.code}
        onChange={(e) => handleCodeChange(e.currentTarget.value)}
        error={form.errors.code || (codeAvailable === false ? 'This code is already in use' : undefined)}
        maxLength={20}
        rightSection={renderAvailabilityStatus(checkingCode, codeAvailable, form.values.code)}
        rightSectionWidth={100}
        required
      />

      <Select
        label="Default Meeting Platform"
        placeholder="Select platform"
        description="Auto-populates meeting links from coach profiles when finalizing sessions"
        data={MEETING_PLATFORM_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
        value={form.values.defaultMeetingPlatform || null}
        onChange={(value) => form.setFieldValue('defaultMeetingPlatform', (value || '') as MeetingPlatform | '')}
        clearable
      />

      {/* Team Assignments */}
      <Box mt="md">
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>
            Team Assignments <Text span c="dimmed" fw={400}>(optional)</Text>
          </Text>
          <UnstyledButton
            onClick={() => {
              // Build lookup: job title name → user IDs with that role
              const roleToUsers = new Map<string, string[]>();
              for (const user of internalUsers) {
                if (user.job_title) {
                  const existing = roleToUsers.get(user.job_title) || [];
                  existing.push(user.id);
                  roleToUsers.set(user.job_title, existing);
                }
              }

              // Fill only unassigned roles
              const updated = { ...form.values.teamAssignments };
              for (const group of jobTitleGroups) {
                for (const title of group.titles) {
                  // Skip if already assigned
                  if (updated[title.id]) continue;

                  const matchingUsers = roleToUsers.get(title.name);
                  // Only auto-fill if exactly one user matches
                  if (matchingUsers && matchingUsers.length === 1) {
                    updated[title.id] = matchingUsers[0];
                  }
                }
              }
              form.setFieldValue('teamAssignments', updated);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <Text size="sm" c="var(--medley-green-primary)" style={{ cursor: 'pointer' }}>
              Auto-populate
            </Text>
            <ArrowRight size={14} color="var(--medley-green-primary)" />
          </UnstyledButton>
        </Group>
        <Text size="xs" c="dimmed" mb="md">
          Assign team members to roles. Tasks will be auto-assigned based on these. You can assign team members later from the cohort detail page.
        </Text>

        {jobTitleGroups.map((group) => (
          <Box key={group.category} className={classes.wizardSection} mb="sm">
            <Text size="sm" fw={600} mb="sm" c={group.disabled ? 'dimmed' : undefined}>
              {group.category}
            </Text>
            {group.disabled ? (
              <Text size="xs" c="dimmed" fs="italic">
                No roles assigned to this function.
              </Text>
            ) : (
              <Stack gap="sm">
                {group.titles.map((title) => (
                  <Select
                    key={title.id}
                    label={title.name}
                    placeholder="Unassigned"
                    size="sm"
                    data={internalUsers.map(user => ({
                      value: user.id,
                      label: user.name,
                    }))}
                    value={form.values.teamAssignments[title.id] || ''}
                    onChange={(value) => {
                      form.setFieldValue('teamAssignments', {
                        ...form.values.teamAssignments,
                        [title.id]: value || '',
                      });
                    }}
                    clearable
                    searchable
                  />
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
